import {QuantumGraph} from 'quantum-graph';
import auth from './auth';
import {generateHash} from './hash';

type QuantumAcceleratorCache = {
    quantumHash: string,
    userId: number,
    timestamp: number,
    response: any
}

class QuantumAccelerator {
    private request: IDBOpenDBRequest | undefined;
    private readonly CACHE_TTL_HOURS = 24;
    private readonly CACHE_TTL_MS = this.CACHE_TTL_HOURS * 60 * 60 * 1000;
    private readonly OBJECT_STORE_NAME = 'quantum_cache';
    private readonly META_KEY = '::metadata';

    constructor() {}

    /**
     * Validates that the current session is valid for cache access.
     * Clears cache if session is invalid or expired.
     * @returns true if session is valid, false otherwise
     */
    private validateSession(): boolean {
        // No token or expired token - clear cache and return false
        if (!auth.token || auth.isExpired()) {
            this.clearObjectStore();
            return false;
        }
        return true;
    }

    private getStore(): Promise<IDBObjectStore> {
        return new Promise((resolve, reject) => {
            this.request = indexedDB.open(this.OBJECT_STORE_NAME, 3);
            this.request.onupgradeneeded = (e: any) => {
                let db: IDBDatabase = e.target.result;
                db.createObjectStore(this.OBJECT_STORE_NAME, {keyPath: 'quantumHash'});
            };
            this.request.onsuccess = (e: any) => {
                let db: IDBDatabase = e.target.result;
                let store = db.transaction(this.OBJECT_STORE_NAME, 'readwrite').objectStore(this.OBJECT_STORE_NAME);
                resolve(store);
            };
            this.request.onerror = () => {
                reject('IndexedDB request error');
            };
        });
    }

    private async generateHash(queryString: string) {
        return await generateHash(queryString);
    }

    private async getFromCache(queryHash: string): Promise<any> {
        // Validate session before accessing cache - clears cache if invalid
        if (!this.validateSession()) {
            return null;
        }

        return new Promise((resolve) => {
            this.getStore().then(store => {
                let cachedQueryRequest = store.get(queryHash);
                cachedQueryRequest.onsuccess = () => {
                    let cachedQuery: QuantumAcceleratorCache = cachedQueryRequest.result;
                    // Verify cache entry belongs to current user
                    if (cachedQuery && cachedQuery.userId === auth.principal?.sub) {
                        const cacheAge = Date.now() - cachedQuery.timestamp;
                        // Check if cache entry is within TTL (24 hours)
                        if (cacheAge < this.CACHE_TTL_MS) {
                            resolve(cachedQuery.response);
                        } else {
                            // Cache expired - return null
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                };
            });
        });
    }

    private addToCache(queryHash: string, response: any, ttlTimestamp?: number) {
        // Don't cache if session is invalid
        if (!this.validateSession()) {
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            this.getStore().then(store => {
                const now = Date.now();
                // Ensure timestamp cannot be set in the future (prevents TTL bypass)
                const safeTimestamp = ttlTimestamp && ttlTimestamp <= now ? ttlTimestamp : now;

                const cachedQuery: QuantumAcceleratorCache = {
                    quantumHash: queryHash,
                    userId: auth.principal!.sub,
                    timestamp: safeTimestamp,
                    response
                };
                store.put(cachedQuery).onsuccess = () => resolve(cachedQuery);
            });
        });
    }

    public async getQuery(query: QuantumGraph): Promise<any> {
        const queryHash = await this.generateHash(JSON.stringify(query.serialize()));
        return this.getFromCache(queryHash);
    }

    public async getMetadata(query: QuantumGraph): Promise<any> {
        const metaHash = await this.generateHash(JSON.stringify(query.serialize()) + this.META_KEY);
        return this.getFromCache(metaHash);
    }

    public async addQuery(query: QuantumGraph, response: any, ttlTimestamp?: number) {
        const queryHash = await this.generateHash(JSON.stringify(query.serialize()));
        return this.addToCache(queryHash, response, ttlTimestamp);
    }

    public async addMetadata(query: QuantumGraph, response: any, ttlTimestamp?: number) {
        const metaHash = await this.generateHash(JSON.stringify(query.serialize()) + this.META_KEY);
        return this.addToCache(metaHash, response, ttlTimestamp);
    }

    public clearObjectStore() {
        // Delete the entire database to ensure complete cleanup
        if (typeof indexedDB !== 'undefined') {
            indexedDB.deleteDatabase(this.OBJECT_STORE_NAME);
        }
    }

    public isIndexedDBSupported(): boolean {
        return 'indexedDB' in window;
    }
}

export default new QuantumAccelerator();